import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { eTeamPermission } from "typings";
import Button, { ButtonProps } from "../components/1_atom/buttons/button";
import DiscordServerButton from "../components/1_atom/buttons/discordServerButton";
import Tag from "../components/1_atom/tag/tag";
import Video from "../components/1_atom/video/video";
import Layout from "../components/3_organism/layout/layout";
import Styles from './extras/styles/documentation.module.scss';

export default function Documentation() {

  const btnProps: Partial<ButtonProps> = {
    color: "white-on-dark",
    variant: "transparent",
    fullWidth: true,
    textAlign: "left"
  }

  const menuItems: {label: string, elemID: string, isSub?: boolean}[] = [
    { label: "What is Prepi?", elemID: "what-is-prepi" },
    { label: "Getting started", elemID: "getting-started" },
    { label: "Signing up", elemID: "signing-up" },
    { label: "Teams", elemID: "teams" },
    { label: "Team management", elemID: "team-management", isSub: true },
    { label: "Signups", elemID: "signups" },
    { label: "Split signups", elemID: "split-signups", isSub: true },
    { label: "Multi character signups", elemID: "multi-character-signups", isSub: true },
    { label: "Real-time and collaboration", elemID: "real-time-and-collaboration" },
    { label: "Automations", elemID: "automations" },
    { label: "Roster & Groups", elemID: "roster-and-groups" },
    { label: "Roles", elemID: "roles" },
    { label: "Smart lists", elemID: "smart-lists" },
    { label: "Create smart lists", elemID: "create-smart-lists", isSub: true },
    { label: "Assignments", elemID: "assignments" },
    { label: "Grid panel", elemID: "grid-panel", isSub: true },
    { label: "Target panel", elemID: "target-panel", isSub: true },
    { label: "Spec swaps panel", elemID: "spec-swaps-panel", isSub: true },
    { label: "Image panel", elemID: "image-panel", isSub: true },
    { label: "Text panel", elemID: "text-panel", isSub: true },
    { label: "Discord bot sync", elemID: "discord-bot" },
    { label: "Time savers", elemID: "time-savers" },
  ]

  const menu = (
    <div className={`${Styles.menu}`}>
      <div className={Styles.menuContent}>
        <h2 className="mb1">Documentation</h2>
        <h4 className="weak-text mb2">
          Can't find what you're looking for?
          <br /> join our Discord!
        </h4>
        <h5 className="mb1">Table of contents</h5>
        <div className="flex-col gap1 mb3">
          {menuItems.map((item) => {
            const btn = (
              <a href={`#${item.elemID}`}>
                <Button
                  {...btnProps}
                  text={item.label}
                />
              </a>
            )
            if (item.isSub) return <div key={item.elemID} className="pl3">{btn}</div>
            return <React.Fragment key={item.elemID}>{btn}</React.Fragment>
          })}
        </div>
      </div>
    </div>
  )

  const Automations = () => {
    return (
      <>
        <h2 id="automations">Automations</h2>
        <p>
          Automations have a start time and a "repeat every _ days" number. You can schedule sequences of actions 
          from the time the automation starts to the time when it repeats. Whenever the automation runs its
          last scheduled sequence, it will renew all the signup IDs and push all the start times forward by 
          the number of days in your "repeat every _ days".
        </p>
        <p><b>Sequences</b></p>
        <p>
          Sequences are ordered lists of steps/ actions. They are executed in the order shown in the UI.
          Sequences can be scheduled at any time after the automation starts and before it repeats.
          You can even schedule sequences when, X hours before or X hours after your signups from 
          Signup Creation either starts or ends. It will show you right next to it the time and date
          when that is.
        </p>
        <p><b>Signup Creation</b></p>
        <p>
          Signup creation is like other sequences but is limited to "use template" actions. To ensure
          that later sequences doesn't try to access or link to signups that don't exist, the signup creation
          sequence runs first and immediately once the automation starts.
        </p>
        <p><b>Execution reporting</b></p>
        <p>
          When running a sequence, each step will record when it was executed and if there were any errors. If There
          were any errors, the reason will be shown below the offending step. Whenever a step runs into an error, the
          sequence will stop and the owner of the automation will be notified by Prepi in a DM. You can change who of the
          admins will be notified when editing the automation.
        </p>
        <h4 id="automations">Types of steps / actions</h4>
        <p><b>Use Template</b> allows you to make a signup based on a template. You can choose a new start time
          and whether or not to keep signups from the template.</p>
        <p><b>Post Signups</b> will post a signup embed in the chosen Discord channel much like the  
          "/signup link" bot command.</p>
        <p><b>Post in channel</b> will post the given text to the chosen Discord channel.</p>
        <p><b>Send DM</b> will send the given text to one of the teams signup managers or assistants.</p>
        <p><b>Request Confirmation</b> will post a request embed in the chosen Discord channel like the 
        "/request confirmation" bot command.</p>
        <p><b>Archive Signup</b> will archive one of the signups created in Signup Creation.</p>
      </>
    )
  }

  const WhatIsPrepi = () => {
    return (
      <>
        <h2 id="what-is-prepi">What is Prepi?</h2>
        <p>
          Prepi is a collaborative real-time web application to build and share assignments with
          your team. It is made to be fast and easy to use, but also powerful and flexible enough
          for more advanced use cases.
        </p>
        <h4 id="Do I need to read this?">Do I need to read this?</h4>
        <p>
          <b>Absolutely not.</b> We'd recommend just jumping into it and playing around with it. You can
          always come back here later if you have a specific question.
        </p>
      </>
    )
  }

  const GettingStarted = () => {
    return (
      <>
        <h2 id="getting-started">Getting started</h2>
        <h4 id="As a leader">As a leader</h4>
        <ol>
          <li>Login with Discord</li>
          <li>(Optional) Invite Discord bot to your server</li>
          <li>Create a new signup</li>
          <li>Edit roles & assignments</li>
          <li>
            Share signup
            <ol>
              <li>Share URL directly with members</li>
              <li>Or use /signup link command with discord bot</li>
            </ol>
          </li>
        </ol>
        <p>You can also of course keep working on assignments after you share the signup.</p>
        <h4 id="As a member">As a member</h4>
        <p>Either you'll see an URL pasted or a signup embed is posted with the discord bot. </p>
        <p>
          <b>Shared URL: </b>
        </p>
        <ol>
          <li>
            Login with discord on <a href="http://prepi.io">prepi.io</a>
          </li>
          <li>Click signup (top right) and fill out the information</li>
        </ol>
        <p>
          <b>Discord bot embed: </b>
        </p>
        <ol>
          <li>Click 1-click signup</li>
          <li>
            follow the link & login with discord on <a href="http://prepi.io">prepi.io</a>
          </li>
          <li>Click signup (top right) and fill out the information</li>
        </ol>
        <p>
          If your signups need characters, you can create them in{" "}
          <Link href="/settings" className="link">
            settings
          </Link>
          .
        </p>
      </>
    )
  }

  const SigningUp = () => {
    return (
      <>
        <h2 id="signing-up">Signing up</h2>
        <h4 id="on-the-web">On the web</h4>
        <p>
          If you're allowed to sign up to a signup you can click the <b>signup</b> button
          (top right).
        </p>
        <h4 id="on-discord">On Discord</h4>
        <p>
          Normally Discord signup embeds will have a few buttons below them:
        </p>
        <ul>
          <li>
            <b>1-click signup</b> will signup with the the main signup set,
            alternatively if no sets are found it will use the character marked as main in{" "}
            <a className="link" href="/settings">settings</a>
            .
          </li>
          <li><b>Signup</b> will give you the option of instant signup or custom signup.
          <ul>
            <li><b>Instant signup</b> will sign you up normally with the selected character.</li>
            <li><b>Custom signup</b> will allow you to customise your signup or to create more signup sets or characters.</li>
          </ul>
          </li>
          <li><b>Absent</b> will sign you as absent. You can also use this button to sign off.</li>
        </ul>
        <h4 id="Signing off">Signing off</h4>
        <p>
          You can sign off by clicking <b>update signup</b> (top right) and selecting{" "}
          <b>sign off</b>.
        </p>
        <h3 id="alternate-character-signup">Multi-character signups</h3>
        <p>
          Some signups will allow you to sign up with more than one character for flexibility. It is then up to the signup organiser to choose the character that fits best.
        </p>
        <h4 id="on-the-web">On the web</h4>
        <ol>
          <li>Click the 'Signup' or 'Update signup' button in the top right corner.</li>
          <li>Click 'add alternate' button beside your preferred character.</li>
          <li>Choose your character by clicking one below.</li>
          <li>You can use a different spec by toggling 'use different spec' and selecting something else.</li>
        </ol>
        <h4 id="on-the-web">On Discord</h4>
        <ol>
          <li>Click the blue 'Signup' button on a signup embed.</li>
          <li>Follow the normal signup process.</li>
          <li>At the end you will be given an option to select up to two alternate characters in a select menu or skip it with the 'Not now' button.</li>
        </ol>
        <h3 id="signup-sets">Signup sets</h3>
        <p>
          Signup sets are pre-saved signup setups you can use to make your signup process even faster. Your main signup set on the server you're trying to sign up for will be used for 1-click signup button.
        </p>
        <p>You can create a signup set either:</p>
        <ul>
          <li>In <a className="link" href="/settings">settings</a> under 'Signup sets'</li>
          <li>While in the signing up window on the web (if you already have a set on that server)</li>
          <li>After having completed a custom signup with alts with the Discord bot.</li>
        </ul>
        <p>You can edit or delete your sets in <a className="link" href="/settings">settings</a>.</p>
      </>
    )
  }

  const Teams = () => {
    return (
      <>
        <h2 id="teams">Teams</h2>
        <h4>Create a team</h4>
        {/* You can only create a Prepi team tied to a Discord server you have <b>'MANAGE_GUILD' permissions</b> on. */}
        <p>You can create a team in two ways:</p>
        <ul>
          <li>Web app: Go to <a className="link" href="/teams">teams</a> and click <b>create team</b> then follow the steps.</li>
          <li>Discord bot: use the /team create command.</li>
        </ul>
        <p>After you've created your team it is recommended you then create some tags, then go to 'team options', 'edit team' and update the section under 'Allow members with these Discord roles to join'.</p>
        <h4>Join a team</h4>
        <p>Go to <a className="link" href="/teams">teams</a> and click <b>join team</b>. Prepi will then look for any teams that are connected to any of the Discord servers you're a part of. If the Prepi team you're looking for is not there you can logout from Prepi and login again and it should appear.</p>
        <h3 id="team-management">Team management</h3>
        <h4>Member management</h4>
        <p>From your teams page, choose <b>members</b> tab then <b>edit member</b>. This will allow you to modify each members tags or remove them from the team. </p>
        <h4>Tag management</h4>
        <p>From your teams page, choose <b>tags</b> tab then <b>edit tags</b>. From here you'll be able to add more tags, turn on and off individual team permissions.</p>
        <h4>Team permissions</h4>
        <p>Different team tags can give you special permissions to create and manage resources on behalf of the team. For instance if you have the <b>manage signups</b> permission, you will see a new option under <b>Create as</b> when creating a new signup.</p>
        <h4>Allow members to join with specific Discord roles</h4>
        <p>First you'll need to create at least one tag. Then click <b>admin options</b>, <b>edit team</b>. Under <b>Allow members with Discord roles</b>, add a new row and fill in the info. You'll need know the ID to the Discord role you want to use. <a href="https://discordhelp.net/role-id">How to find Discord role ID</a>.</p>
      </>
    )
  }

  const Signups = () => {
    return (
      <>
        <h2 id="signups">Signups</h2>
        <h3 id="signup-management">Signup management</h3>
        <h4 id="Creating a signup">Creating a signup</h4>
        <p>
          You can create a new signup by going to{" "}
          <Link href="/signups" className="link">
            signups
          </Link>{" "}
          and clicking <b>new signup</b>. Most of the options are fairly self-explanatory but for
          those who are not:
        </p>
        <p>
          <b>Template: </b> pre-defined roles and assignments.
        </p>
        <p>
          <b>New signups goes to: </b> choosing <b>roster first</b> will put new signups directly on
          the roster until the roster fills up. <b>Bench</b> will set new signups as benched and it
          is up to the admins to manually make sure the right members are put on the roster.
        </p>
        <p>
          <b>Signup visibility:</b>
        </p>
        <ul>
          <li>
            <b>Open</b> which will not limit who can see the signup data in any way
          </li>
          <li>
            <b>On roster</b> which will only show content to members on the roster
          </li>
          <li>
            <b>Admin</b> will only show content to admins
          </li>
        </ul>
        <p>
          Signups you create on prepi.io will automatically include roles and assignments. Signups
          made through the discord bot will not include roles and assignments until you scroll to
          the bottom of the signup and click add roles and assignments.
        </p>
        <p>
          <Link href="/bot#signup-create" className="link">
            How to create signups directly from discord.
          </Link>
        </p>
        <h4 id="templates">Templates</h4>
        <p>
          Templates are documents that when used will copy roles and assignments into a new signup.
          There are some pre-defined templates for you to play around with, but you can also create
          your own custom templates to re-use.
        </p>
        <p>
          <b>How to create a template</b>
        </p>
        <ol>
          <li>Navigate to the signup you want to use as a template</li>
          <li>
            Make sure you are either the signup owner or have '{eTeamPermission.MANAGE_SIGNUPS}'
            team permission.
          </li>
          <li>
            Open the{" "}
            <Button
              text="Admin options"
              iconRight={{ icon: "arrow-right", addClass: "r90", fill: false }}
              size="mini-sq"
              variant="soft"
              style={{ display: "inline-flex" }}
              addClass="mb1"
              color="white-on-dark"
            />{" "}
            dropdown
          </li>
          <li>
            Select 'Template' then{" "}
            <Button
              text="Convert to template"
              size="mini-sq"
              variant="transparent"
              style={{ display: "inline-flex" }}
              addClass="mb1"
              color="white-on-dark"
            /> If 'Template' option doesn't appear, it might be because that signup
            doesn't have any assignments.
          </li>
        </ol>

        <p>
          <b>How to create a public template</b>
        </p>
        <p>
          Public templates are different from normal templates in that they can be copied by anyone,
          regardless of access level. They can also not be signed up to by anyone, does not have a
          roster or group comp.
        </p>
        <ol>
          <li>Navigate to the template you want to make public</li>
          <li>
            Make sure you are either the signup owner or have '{eTeamPermission.MANAGE_SIGNUPS}'
            team permission.
          </li>
          <li>
            Open the{" "}
            <Button
              text="Admin options"
              iconRight={{ icon: "arrow-right", addClass: "r90", fill: false }}
              size="mini-sq"
              variant="soft"
              style={{ display: "inline-flex" }}
              addClass="mb1"
              color="white-on-dark"
            />{" "}
            dropdown
          </li>
          <li>
            Select 'Template' then{" "}
            <Button
              text="Make template public"
              size="mini-sq"
              variant="transparent"
              style={{ display: "inline-flex" }}
              addClass="mb1"
              color="white-on-dark"
            />
          </li>
        </ol>
        <p>
          <a href="#cell-memory" className="link">
            How to save time with templates
          </a>
        </p>
        <h4 id="Duplicating a signup">Duplicating a signup</h4>
        <p>
          If you need a backup or something to refer back to you can signups by clicking{" "}
          <b>admin options</b>, selecting <b>duplicate signup</b> and giving it a new title and/or
          start time. You will be the owner of the new duplicated signup.
        </p>
        <h4 id="archive">Archiving</h4>
        <p>
          Non-template events will be{" "}
          <b>automatically archived 14 days after an event has concluded</b>. Archiving means the{" "}
          <b>roles and assignments will be deleted</b> and the signup will move to the archive
          section in the signups overview. If you have a signup you want to keep roles and
          assignments data on - you need to convert it to a template.
        </p>

        {/* SPLIT SIGNUPS */}
        <h2 id="split-signups">Split signups</h2>
        <img
          src="/img/docs/splits/splits.png"
          alt="Five runs inside two time windows"
          className="mt2 mb2"
        />
        <h4 id="Update roster">What is a split signup?</h4>
        <p>
          A split signup is a way to let members sign up to more than one time window, and then let
          the admins put whichever characters in however many runs the admin wants. <br />A{" "}
          <b>split signup</b> has one or more <b>time windows</b>.<br />A time window has one or
          more <b>sessions</b>.<br />A session has one or more <b>runs</b>.<br />
        </p>
        <p>
          <b>Time windows</b>
        </p>
        <p>
          A time window is a period of time a member can sign up for. Admins can schedule as many
          sessions and runs within the time window as they want. A members characters can be moved
          around freely within the time windows the member signed up for. Each time window will get
          a color, and admins can see which characters can be moved into which time windows by
          looking at the colored stripes on the characters on the bench.
        </p>
        <p>
          <b>Sessions</b>
        </p>
        <p>
          A session is a period of time within a time window. There must be at least one session in
          a time window, but there is no upper limit. A member can maximum have one character inside
          a session.
        </p>
        <p>
          <b>Runs</b>
        </p>
        <p>
          Runs are the actual events. When any admin publishes for the first time, runs will be
          turned into individual signup documents. Likewise if a run is removed from the split
          signup and an admin publishes, the run document is deleted. When a run is created, it will
          copy properties from the split signup such as max size, title, roles and assignments from
          the split signup. Run members will always be updated to each run document when publishing.
        </p>
        <p>
          <b>Rules</b>
        </p>
        <ul>
          <li>
            A members characters can only be put in sessions inside the time windows the member
            selected.
          </li>
          <li>A member can only be in 1 run per session.</li>
          <li>You cannot sign up to directly to a run, only the main split signup.</li>
        </ul>
        <h4 id="Update roster">Create or edit a split signup</h4>
        <p>
          Split signups may not be available to all users. You may create a split signup by
          following the steps below:
        </p>
        <ol>
          <li>
            Open the{" "}
            <Button
              text="Admin options"
              iconRight={{ icon: "arrow-right", addClass: "r90", fill: false }}
              size="mini-sq"
              variant="soft"
              style={{ display: "inline-flex" }}
              addClass="mb1"
              color="white-on-dark"
            />{" "}
            dropdown
          </li>
          <li>
            Select{" "}
            <Button
              text="Split signup"
              iconLeft={{ icon: "split-signup" }}
              size="mini-sq"
              variant="transparent"
              style={{ display: "inline-flex" }}
              addClass="mb1"
              color="white-on-dark"
            />
          </li>
          <li>
            Create as many time windows and sessions within those time windows as you want. By
            default there will always be two runs within the first time window.
          </li>
        </ol>
        <h3>Split signup distribution</h3>
        <p>
          You can utilize smart lists to define how you want Prepi to distribute your benched
          characters across your split runs. To learn more about smart lists,{" "}
          <a href="#smart-lists" className="link">
            click here
          </a>
          .
        </p>
        <p>
          <b>Run size</b>
        </p>
        <p>
          You can modify the values based on different run sizes: default, 25 man and 10 man.
          Whenever you update a value in default, only the default value will be set. If you update
          a value in 10 man, both the 10 man and the default value will be set.{" "}
          <b>
            If a value is set by the run size you're currently watching it will be marked blue, if
            it is green it's a value inherited from the default
          </b>
        </p>
        <p>
          <b>Target</b>
        </p>
        <p>
          Indicates how many characters with particular traits you want in each run. The
          distribution algorithm will only add characters if it has less characters in a run that
          matches. Meaning, if character Z has traits A and B and an earlier smart list wants 2 of
          trait A, and a later smart list wants 1 of trait B. The later smart list won't have to add
          any characters since character Z was already added and has trait B. A smart list target
          can work in a few different ways:
        </p>
        <ul>
          <li>
            <b>Defined {">"} 0 (blue)</b> - The smart list will add characters until there are that
            many characters that match that smart list.
          </li>
          <img
            src="/docs/split-distribution/target-max.jpg"
            alt="Maximum target"
            style={{ maxWidth: 200 }}
            className="mt2 mb2 rounded"
          />
          <li>
            <b>Defined as 0 (blue)</b> - Nothing will be added.
          </li>
          <li>
            <b>Undefined with content targets (grey)</b> - The smart list will try to add exactly
            the targets each item in the smart list.
          </li>
          <img
            src="/docs/split-distribution/target-content-limited-only.jpg"
            alt="Maximum target"
            style={{ maxWidth: 200 }}
            className="mt2 mb2 rounded"
          />
          <li>
            <b>Undefined without content targets (grey)</b> - Nothing will be added.
          </li>
        </ul>
        <p>
          You can add targets directly to the individual items inside a smart list. These targets
          will be tried to be met first and exactly. If the smart list target is defined and over 0
          and the target is not met from the items targets, the distribution algorithm will then try
          to add from the remaining pool of characters from the whole smart list.
        </p>
        <p>
          <b>Priority</b>
        </p>
        <p>
          The higher the priority of a smart list, the earlier the distribution algorithm will try
          to place it's characters.
        </p>
        <p>
          <b>Character appeal</b>
        </p>
        <p>
          Everytime the algorithm tries to place one of several otherwise equal characters in a run,
          it will choose the one with the highest character appeal. Priority and character appeal
          values are added together from every smart list the character is in for the final value.
        </p>
        <p>
          <b>Options</b>
        </p>
        <ul>
          <li>
            <b>Finish using legacy distribution algorithm</b> - If a run isn't full after smart list
            distribution, it will randomly spread tanks, healers, melee and ranged into runs.
          </li>
          <li>
            <b>Use characters with negative appeal</b> - If disabled, all characters with negative
            appeal will be skipped.
          </li>
        </ul>

        {/* MULTI-CHARACTER SIGNUPS */}
        <h2 id="multi-character-signups">Multi character signups</h2>
        <h4 id="quick-swap">Switching between characters</h4>
        <p>
          To quickly swap between which of the members characters you'd like to use, simply{" "}
          <b>hover over a member in the roster and click one of the icons on the right</b>. You can
          see which of your members have signed with alternate characters with the <b>+1 / 2</b>{" "}
          mark.
        </p>
        <h4 id="show-all-alternate-characters">Show all alternates</h4>
        <p>
          If your signup allows for multi character signups (enabled by default) you will see a
          toggle to <b>show x alternates</b> top right on the roster. Clicking this will show you
          all the alternate characters which are marked with the link icon. When you're in this mode
          you can hover over any character and any of that members character will be highlighted so
          you can easily who's connected to who.
        </p>
        <p>
          All the normal interactions still work as normal, with add & remove buttons and drag
          replace. However if you try to drag an alternate character of a member that's already on
          the roster, it will simply switch that members main and alternate characters.
        </p>
      </>
    )
  }

  const RealtimeAndCollaboration = () => {
    return (
      <>
        <h2 id="real-time-and-collaboration">Real-time & collaboration</h2>
        <p>
          The document owner and admins automatically sync all changes between each other. Those
          changes will not propagate to non-admins before one of the admins click{" "}
          <b>save and publish</b>.
        </p>
        <h4 id="Adding and removing admins">Adding and removing admins</h4>
        <p>
          As an owner you can click on a member on the roster and select{" "}
          <b>promote admin</b> to add a new admin. Clicking on the admins below the signup
          title will allow you to remove them again.
        </p>
        <h4 id="Owner only actions">Owner only actions</h4>
        <p>
          Adding or removing admins, deleting or archiving the signup are owner only actions that
          admins cannot perform. Everything else can be done by an admin or the owner.
        </p>
        <h4 id="Admin options">Admin options</h4>
        <p>
          <b>Archive</b> - when you're done with a signup and no longer need assignments,
          but want to keep the signups and group comp. This will delete roles and assignments.
        </p>
        <p>
          <b>Ghostify signups</b> - an alternative to clear signups. It makes all members
          only visible by admins in the web app.
        </p>
      </>
    )
  }

  const RosterAndGroups = () => {
    return (
      <>
        <h2 id="roster-and-groups">Roster & Groups</h2>
        <p>
          All your members and placeholders will display in their respective game role based column
          on the roster. Groups will automatically populate based on which members are on the
          roster. Drag your members around as you want.
        </p>
        <h4 id="Update roster">Update roster</h4>
        <p>
          Enable <b>edit roster</b>
        </p>
        <ul>
          <li>Add to roster: click on green plus button on a benched member</li>
          <li>Remove from roster: click on red minus button on member that is on roster</li>
          <li>
            Replace member: drag benched member onto the member that is already on the roster. Doing
            so will also replace any mention of the replaced member in the rest of the document.
          </li>
        </ul>
        <h4 id="adding-placeholders">Adding placeholders</h4>
        <p>
          Placeholders are useful if a member has communicated in other ways intention to attend but
          haven't signed up through Prepi or to just play around with.
        </p>
        <p>There's three ways of creating placeholders</p>
        <ul>
          <li>Clicking the <b>add placeholders</b> button in the roster when the signup is empty</li>
          <li>Clicking <b>get 25 placeholders</b> under admin options in the top right</li>
          <li>
            Clicking <b>create custom placeholders</b> then selecting a specialisation and
            typing the name of the placeholder to create a single placeholder
          </li>
        </ul>
        <p>
          You can copy your group composition as csv and import the text into game addons with csv
          import support to automatically re-order your in-game groups with 1 click.
        </p>
        <h4 id="roster-color-dots">Roster colored dots</h4>
        <p>
          You may see two colored dots next to a signup members name in the roster. If you've turned on signup confirmations you will see either an orange or green dot on non-placeholder members: green means confirmed, orange means not confirmed. Blue dots are for members marked as standby.
        </p>
      </>
    )
  }

  const Roles = () => {
    return (
      <>
        <h2 id="roles">Roles</h2>
        <p>
          Roles are the middle step between your people and what you want them to do. If you have
          repeating actions across multiple places which you need the same member to do, create a
          role and re-use that role in all those places.
        </p>
        <p>
          To minimise assignment maintenance, it is recommended to use roles as much as possible
          when building your assignments. That doesn't mean always, but it usally turns out to be quite close.
        </p>
        <p id="create-a-role"><b>Create a role</b></p>
        <ol>
          <li>Enable edit mode</li>
          <li>Write a label for the new role and select what kind of role it is</li>
          <li>Click add new role</li>
        </ol>
        <p id="add-a-member-reference"><b>Add a member reference</b></p>
        <p>There's two ways of adding a member reference</p>
        <ul>
          <li>Drag a member from the roster or groups into the role</li>
          <li>
            Click into the open space inside the role to open{" "}
            <a href="#replace-with" className="link">
              replace with
            </a>
            , then start typing what you're looking for.
          </li>
        </ul>
        <h4 id="Role usage">Role usage</h4>
        <p>To reference a role in assignments you can either</p>
        <ul>
          <li>Drag a role into a valid cell in assignments</li>
          <li>
            Either click an open cell in assignments or click an occupied cell and select{" "}
            <a href="#replace-with" className="link">
              replace with
            </a>
            , then type what you're looking for.
          </li>
          <li>
            Click role label and select <b>copy to</b>, then select the cell you want to
            copy into.
          </li>
        </ul>
      </>
    )
  }

  const Assignments = () => {
    return (
      <>
        <h2 id="assignments">Assignments</h2>
        <p>
          There are two main parts of assignments: General and Specific. General assignments will
          always be visible. Specific assignments will only show the content of the currently
          selected section. Assignments content is divided into different panels. Panel
          options include{" "}
          <a href="#grid-panel" className="link">
            grid
          </a>
          ,{" "}
          <a href="#target-panel" className="link">
            target
          </a>
          ,{" "}
          <a href="#text-panel" className="link">
            text
          </a>
          ,{" "}
          <a href="#image-panel" className="link">
            image
          </a>
          ,{" "}
          <a href="#spacer-panel" className="link">
            spacer
          </a>
          .
        </p>
        <h4 id="specific-assignment-sections">Specific assignment sections</h4>
        <p>
          Specific assignment sections allow you to create and organise endlessly nested content and get back
          to it quickly.
        </p>
        <h5 id="Add new sections">Add new sections</h5>
        <ol>
          <li>Enable edit mode</li>
          <li>
            Click on one of the plus buttons
            <ol>
              <li>Scale horizontally by clicking the ones to the right of your current sections</li>
              <li>
                Scale vertically (more nested) by clicking the button below your current sections.
              </li>
            </ol>
          </li>
          <li>Write a fitting section label and click enter</li>
        </ol>
        <h5 id="Edit existing section">Edit existing section</h5>
        <ol>
          <li>Click a selected section</li>
          <li>
            From there you can
            <ol>
              <li>Rename</li>
              <li>Move the section left or right</li>
              <li>Or delete the section and all its content</li>
            </ol>
          </li>
        </ol>
        <h4 id="Assignment panels">Assignment panels</h4>
        <h5 id="Add new panel">Add new panel</h5>
        <ol>
          <li>Enable edit mode</li>
          <li>Click one of the options inside the box with dashed borders</li>
        </ol>
        <h5 id="Edit panel">Edit panel</h5>
        <p>
          You can move the panel left or right by clicking the arrows on the top left side of the
          panel. You can see each panels order number between the arrows. All panels try to be sorted
          top left, lower numbers having priority. However if there is an empty spot, panels with higher
          numbers that can fit into the spot will automatically flow upwards to fill empty spots. You can
          use spacer panels to counteract this if you have a specific panel layout you're going for.
        </p>
        <p>Delete the panel by clicking the red trash can on the top right.</p>
        <p>
          You can make the panel bigger or smaller by clicking the <b>triple dot menu</b>{" "}
          and selecting <b>enlarge</b> or <b>minimise</b>. Some panel types may
          have a required minimum size depending on the content inside the panel.
        </p>
        <h5 id="Copy panel">Copy panel</h5>
        <ol>
          <li>Click the triple dot button above the panel you want to copy</li>
          <li>Click copy panel</li>
          <li>Select one of the boxes that have now lighted up</li>
        </ol>
        <h5 id="Link panel">Link panel</h5>
        <p>
          Linked panels will change the data on all spots that is linked to the panel. When trying
          to delete a linked panel it will ask you if you want to delete only a single panel or all
          the linked panels.
        </p>
        <ol>
          <li>Click the triple dot button above the panel you want to copy</li>
          <li>Click link panel</li>
          <li>Select one of the boxes that have now lighted up</li>
        </ol>
        <p>You cannot link a panel into the same section.</p>
        <h4 id="only-my-assignments">Only my assignments</h4>
        <p>
          Clicking this button will automatically filter away all the noise from any filterable
          panel type.{" "}
        </p>
        <p>Grid panels filter modes:</p>
        <ol>
          <li>Horizontal - will keep any rows you're referenced</li>
          <li>Vertical - will keep any columns you're referenced</li>
          <li>Single cell - will keep only the cells you're referenced</li>
        </ol>
        <p>Keep in mind that the left-most column and column headers will always be visible.</p>
        <div className="flex-row align-center gap2" style={{ marginTop: "1.75rem" }}>
          <h3 id="grid-panel" style={{ margin: 0 }}>
            Grid
          </h3>
          <a href="#only-my-assignments">
            <Tag text="filterable" color="secondary" />
          </a>
        </div>
        <p>
          A grid assignment panel is like a simplified mini spreadsheet. Grids consist of columns
          with a column header and rows of cells below. Grid cells can contain text, icons or
          members (& roles).
        </p>
        <p id="fill-empty-cell"><b>Fill empty cell</b></p>
        <ol>
          <li>Click on the cell you want to fill</li>
          <li>Select one of the different options</li>
        </ol>
        <p id="edit-cell-type"><b>Edit cell type</b></p>
        <ol>
          <li>Hover over the cell you want to edit</li>
          <li>Click the edit / pen icon on the right</li>
          <li>Select a new cell type</li>
        </ol>
        <p id="fill-empty-member-cell"><b>Fill empty member cell</b></p>
        <p>Either</p>
        <ul>
          <li>drag role or member into cell</li>
          <li>click into the cell and start typing in{" "}
          <a href="#replace-with" className="link">
            replace with
          </a></li>
        </ul>
        <h5 id="Editing grid layout">Editing grid layout</h5>
        <p>
          Click <b>edit grid</b> on the grid panel you want to edit.
        </p>
        <p>
          Add row or column by clicking the plus buttons to the right or below the grid. The new row
          or column will use the cell type from the previous row or column.
        </p>
        <p>To move, duplicate or delete columns or rows:</p>
        <ol>
          <li>First select the rows or columns you want to edit</li>
          <li>
            then either
            <ol>
              <li>Click the arrows to bump up/down or left/right.</li>
              <li>Click the copy button to duplicate.</li>
              <li>Click the red trash can button to delete</li>
            </ol>
          </li>
        </ol>
        <h5 id="Game icons">Game icons</h5>
        <p>
          Game icons are supported in column headers, text and icon cells and target assignments as well.
          Just search for what you want. There's thousands of possibilities.
        </p>
        <h5 id="Grid sizes">Grid sizes</h5>
        <p>
          Grids will not allow you to minimise beyond a point. The more grid columns you have, the
          bigger it needs to be.
        </p>
        <p>
          One thing to note about grid sizes are the longer they are horizontally, the more awkward
          they will look on mobile devices. If this is any concern to you, see if you can split the
          grids into smaller grids.
        </p>
        <div className="flex-row align-center gap2" style={{ marginTop: "1.75rem" }}>
          <h3 id="target-panel" style={{ margin: 0 }}>
            Target
          </h3>
          <a href="#only-my-assignments">
            <Tag text="filterable" color="secondary" />
          </a>
        </div>
        <p>
          A target assignment panel is a single column grid. Use it when you need some simple
          assignments where multiple people have to do something around another member. You can add
          an icon by clicking the empty box to the left of the main cell.
        </p>
        <div className="flex-row align-center gap2" style={{ marginTop: "1.75rem" }}>
          <h3 id="spec-swaps-panel" style={{ margin: 0 }}>
            Spec Swaps
          </h3>
          <a href="#only-my-assignments">
            <Tag text="filterable" color="secondary" />
          </a>
        </div>
        <p>
          A spec swaps panel is a quick way to show spec swaps for a particular section. 
          Click 'add swap' to search for a member, then click on the spec you'd like the 
          member to swap to. It will automatically select a spec that has a different game role
          if one is available.
        </p>
        <h3 id="image-panel">Image</h3>
        <p>
          You can link to an existing image by supplying the URL directly to any image on the
          internet. Then resize the panel to the size you want.
        </p>
        <p>
          Prepi does not provide direct file uploads (yet), so feel free to use your favourite free
          image hosting platform.
        </p>
        <h3 id="text-panel">Text</h3>
        <p>
          Prepi comes with a simple collaborative text editor you can use to convey your ideas in
          text format. The text editor is collaborative, so multiple users can edit the same text at
          the same time. You can also use undo (ctrl/cmd + z) and redo (ctrl/cmd & shift + z) on
          your own edits.
          {/* Emoji & game icons are supported by wrapping your desired desired glyph in
          colons like so <code className="code-inline">:skull: :cross:</code>. */}
        </p>
        <h3 id="spacer-panel">Spacer</h3>
        <p>
          If you're trying to create a specific panel layout you can use spacers to change the panel
          flow. A desktop sized monitor will normally show 4 panel columns. A mobile device will
          only show one.
        </p>
        <p>
          Choose between a spacer with a set column span or a spacer which will fill the rest of the
          row.{" "}
        </p>
        <p>
          The <b>rest of row</b> spacer will disappear when it's no longer needed. Say you
          want to utilise the first 3 of 4 columns, you can put a <b>rest of row</b>{" "}
          spacer in the 4th column and when seen on a device that can fit 3 columns, the spacer will
          be hidden.
        </p>
      </>
    )
  }

  const SmartLists = () => {
    return (
      <>
        <h2 id="smart-lists">Smart lists</h2>
        <div className="responsive-iframe-container mt2">
          <iframe
            className="responsive-iframe"
            src={`https://www.youtube.com/embed/Zp4iwd8SGlw`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Embedded youtube"
          />
        </div>
        <p>
          You can use smart lists to create assignments that adapt to your roster based on
          conditional logic. Create the setup you want and let Prepi take care of your assignments.
        </p>
        <Video
          src="/docs/smart-lists/add-instance.mov"
          autoPlay
          loop
          controls={false}
          playbackSpeed={0.9}
        />
        <p>
          <b>Good to know</b>
        </p>
        <ul>
          <li>Members can only appear once inside a smart list.</li>
          <li>
            There's no limit to how many times you can add the same smart list into a grid panel.
          </li>
        </ul>
        <p>
          <b>Using smart lists in assignments</b>
        </p>
        <ol>
          <li>Enable edit mode</li>
          <li>Click in an empty grid member cell and select your smart list</li>
          <li>Drag the smart list instance over the area you want</li>
          <li>
            Optional: change the spread direction by clicking the button top left of the smart list
            instance.
          </li>
        </ol>
        <p>
          The smart list instance will then spread members into any non-filled cell. Types of
          non-filled cells:
        </p>
        <ul>
          <li>Empty member cells</li>
          <li>Cells that haven't been filled by higher priority smart list instances</li>
          <li>Cells that has a role that is empty</li>
        </ul>
        <p>
          Think of it this way “if there's nothing else in the cell, the smart list will fill it”.
        </p>
        <p>
          <b>Moving and resizing</b>
        </p>
        <p>
          To move the smart list instance you drag the instance button in the top left of the cells.
          To resize it, you drag the handle in the bottom right.
        </p>
        <h4 id="spread-priority">Spread priority</h4>
        <p>
          Whenever you add another smart list instance to a grid panel, it will be added to the
          priority list. The instances with the highest priority will be filled first and any filled
          cells will be skipped by lower priority smart list instances. You can change the spread
          priority by clicking the button at the center top of the grid panel and drag and drop the
          order to your liking. There is no limit to how many smart list instances you can add to a
          panel. Multiple instances of the same smart list is allowed. You can create extremely
          intricate assignments this way just by adding different smart list instances and
          prioritising them.
        </p>
        <Video src="/docs/smart-lists/spread-priority.mov" />
        <h4 id="smart-list-instances-overrides">Overriding smart lists</h4>
        <p>
          After you've put a smart list into assignments, there are a few ways you can customise it
          further.
        </p>
        <p>
          <b>Spec swap panel</b>
        </p>
        <p>
          Spec swap panel will change a members spec in all the specific assignments panels you can
          see at the same time. It will not affect general assignments and it will also not affect
          any other section. If you've broken down an encounter into sub-phases, you can use linked
          panels to keep each sub-phase spec swap panel in sync.
        </p>
        <Video src="/docs/smart-lists/spec-swap.mov" />
        <p>
          <b>Overriding a specific cell</b>
        </p>
        <p>
          If you need to override a cell you can click it and change the content of it. If you fill
          the cell, the smart list instances will then skip it. Overriding with a member will only
          show if the member is on roster. This isn't only useful for last minute changes, but also
          helpful to create more deterministic output. It's often helpful to allow members to have
          the same positioning and perform the same actions to build muscle memory.
        </p>
        <Video src="/docs/smart-lists/override.mov" />
        <h4 id="smart-lists-pro-techniques">Pro techniques</h4>
        <ul>
          <li>
            layer two or more smart list instances on top of each other to use as many of the
            preferred members you have, then fill with less preferred members.
          </li>
          <li>
            To create self levelling columns where you want to put particular members in one column:
            you can add each rows rules before the next. For instance: List A in column 1, list B in
            column 2, list C in column 3, then repeat for every row
          </li>
          <li>
            use cell overrides to create make sure people are placed in the same spot every time.
          </li>
        </ul>
        <h3 id="create-smart-lists">Create smart lists</h3>
        <p>
          Custom smart lists may not be available to all users. You may create a smart list by
          following the steps below:
        </p>
        <ol>
          <li>Enable edit mode and </li>
          <li>Navigate to Smart lists</li>
          <li>Click "Add Smart List"</li>
        </ol>
        <p>Smart lists can contain the following types of content:</p>

        <p>
          <b>Link</b>
        </p>
        <p>
          A link is a link to another smart list. It will add the members in the same order from the
          linked smart list as long as the member isn't already used. A member can only appear once
          in a smart list.
        </p>

        <p>
          <b>Sublist</b>
        </p>
        <p>
          Sublists are a way to add members based on conditional logic. Add conditions and set your
          “filter match” to any or all and the sublist will automatically add members to the smart
          list results. See{" "}
          <a href="#smart-lists-conditions" className="link">
            conditions
          </a>{" "}
          &{" "}
          <a href="#smart-lists-filtering" className="link">
            filtering
          </a>{" "}
          below for more info.
        </p>

        <p>
          <b>Character</b>
        </p>
        <p>
          If you want to add a character manually into a smart list, you can add one by clicking “+
          Character”, searching for the one you want and selecting to add it. For the character to
          be added into the results of the smart list, the member has to be:
        </p>
        <ul>
          <li>Signed up with that particular character</li>
          <li>Be on the roster.</li>
        </ul>

        <h4 id="smart-lists-conditions">Conditions</h4>
        <p>List filter conditions and sublist conditions work the same way.</p>
        <ol>
          <li>Click on the “Add list filter” or “Add condition” to add a new condition.</li>
          <li>
            Search for a condition such as
            <ul>
              <li>Class</li>
              <li>Spec</li>
              <li>Game role</li>
              <li>You can also use all conditionals in reverse by searching for “not: __“</li>
            </ul>
          </li>
          <li>Select the conditional to add it</li>
        </ol>
        <h4 id="smart-lists-filtering">Filtering</h4>
        <p>
          <b>List filter</b>
        </p>
        <p>
          Use list filters to filter out potential members inside the whole smart list. This is
          especially useful if you want to tweak another smart list by adding a new smart list,
          linking to the old one and adding a list filter.
        </p>
        <p>
          <b>Limit</b>
        </p>
        <p>
          You can add limits to each smart list or sublist to control how many members it results
          to.
        </p>
        <p>
          <b>Filter match</b>
        </p>
        <ul>
          <li>“All conditions” will add a member if all of the conditions are met</li>
          <li>“Any condition” will add a member as long as any of the conditions are met</li>
        </ul>
        <p>
          The default filter match is any, please keep it in mind when using “not conditionals” as
          it may add more members than anticipated.
        </p>
      </>
    )
  }

  const DiscordBotSync = () => {
    return (
      <>
        <h2 id="discord-bot">Discord bot sync</h2>
        <p>Discord embeds updates in two different ways</p>
        <p><b>User interacts with Discord embed</b></p>
        <p>Interacting with an embed in Discord will <b>always</b> update the embed data.</p>
        <p><b>User interacts with web app</b></p>
        <p>Whenever an admin uses '/signup link', '/request confirmation' or other commands that link to signup data, Prepi will save that embed and update it when changes happen through the web app. The number of embeds you can link to at once may depend on your teams feature access level.</p>
      </>
    )
  }

  const TimeSavers = () => {
    return (
      <>
        <h2 id="time-savers">Time savers</h2>
        <h4 id="replace-with">Replace with</h4>
        <p>
          <b>Replace with</b> is an auto-complete search field that you can use to quickly
          fill or replace a cell reference. You can search for member character names, classes,
          specs and roles.
        </p>
        <h4 id="cell-memory">Cell memory</h4>
        <p>
          All role and assignment cells remember their previous role or member references even if
          the member signs off or the role is emptied and the cell looks empty. Why is this
          important? Because say you clear all signups and people sign up again, all the smart lists, roles and
          assignments will be automatically populated as they were before you cleared signups.{" "}
        </p>
        <p>
          Because of this we recommend using an assignment document at least once before turning it
          into a template. When you then use that template to create new assignments you won't have
          to drag back the members each week.
        </p>
      </>
    )
  }

  return (
    <Layout maxLayoutWidth="medium">
      <Head>
        <title>Documentation</title>
        <link rel="canonical" href={"https://prepi.io" + useRouter().pathname} />
        <meta name="description" content="Wondering how to get started with Prepi? Take a look in the documentation on how to use Prepi's web app and it's many features." />
      </Head>
      <div className={`${Styles.contentContainer} flex-row`}>
        {menu}
        <article className={Styles.content} style={{ flex: 7 }}>
          <WhatIsPrepi />
          <GettingStarted />
          <SigningUp />
          <Teams />
          <Signups />
          <RealtimeAndCollaboration />
          <Automations />
          <RosterAndGroups />
          <Roles />
          <SmartLists />
          <Assignments />
          <DiscordBotSync />
          <TimeSavers />
        </article>
      </div>
      <div className={Styles.discordServer}>
        <h5>Can't find what you're looking for?</h5>
        <p className="weak-text text-center">Feel free to ask any questions on our discord server!</p>
        <DiscordServerButton />
      </div>
    </Layout>
  );
}
